import { create } from 'zustand';

import { de } from '../i18n/de';
import {
  keepDestination,
  keepStillOffered,
  phraseFromKept,
  type KeepOffer as Offer,
} from '../core/authoring/keep';
import { categoryRepo, phraseRepo } from '../data/repositories';
import { useComposerStore } from '../core/composer/useComposer';
import { ActivateButton } from './ActivateButton';

/**
 * `Behalten` — the sentence that was just spoken becomes a tile (`AUT-05`, ADR-041).
 *
 * The best possible source of a phrase is the one the person just composed, and until now it was the one
 * source unavailable: a tile could only be born in care mode, or by mining after the same sentence had
 * been spoken three times in a fortnight.
 *
 * ── Why it is in the band's frame and not on the output bar ──
 *
 * The obvious placement is beside the sentence, and it is the expensive one: the output bar is a single
 * activation target whose whole area opens the partner view, and `SUR-20` wants to split that same target
 * for `Löschen`. ADR-041 put this here instead, so the bar stays one target and `SUR-20` keeps its own
 * arithmetic. It follows the layer switch (ADR-039) for the reason the switch is there: a control that
 * must be reachable whatever the band is showing cannot live in a precedence that hands the band to the
 * candidates the moment anything is typed.
 *
 * ── Why it is never on the speech path ──
 *
 * A confirmation between a person and their voice is the one thing this app never adds. So this is an
 * *offer beside the output*: speaking is untouched, and keeping is one press afterwards with no
 * confirmation of its own. It works whether or not the sentence is still standing, because
 * `keepTextAfterSpeak` (`CMP-07`) changes which tenant the band holds and a frame control does not care.
 *
 * ── Why the state is in a store rather than in the component ──
 *
 * The same reason `RestoreOffer` gives: `PredictionRow` owns the band and has to know whether the offer
 * applies *before* deciding to render it, and a component cannot answer the question that decides whether
 * it mounts. The destination is resolved when the offer is raised rather than when it is pressed, so the
 * press itself can never fail — see `keepDestination`.
 */

interface OfferState {
  offer: Offer | null;
}

const useOfferState = create<OfferState>(() => ({ offer: null }));

/**
 * Raise the offer for a sentence that has just been spoken.
 *
 * Called after speech has been dispatched and history written, never before: this must not delay a
 * syllable. The reads are the caregiver's content, which is small, and nothing waits on them — if the
 * board has no room, the offer is simply never raised.
 */
export function offerKeep(sentence: string): void {
  void Promise.all([categoryRepo.all(), phraseRepo.all()]).then(([categories, phrases]) => {
    const home = keepDestination(categories, phrases);
    if (!home) {
      useOfferState.setState({ offer: null });
      return;
    }

    useOfferState.setState({
      offer: {
        sentence,
        categoryId: home.id,
        // Read now rather than passed in: `commit()` has already appended the space on the
        // `keepTextAfterSpeak` path, and this has to match what a keypress will change.
        textAtOffer: useComposerStore.getState().state.text,
      },
    });
  });
}

/** Whether the offer applies, and for which sentence. Read by the band's owner to decide the frame. */
export const useKeepOffer = (): Offer | null => {
  const offer = useOfferState((state) => state.offer);
  const text = useComposerStore((state) => state.state.text);
  return keepStillOffered(offer, text);
};

export function KeepOffer(): React.JSX.Element | null {
  const offer = useKeepOffer();

  if (!offer) return null;

  const keep = (): void => {
    void phraseRepo.all().then(async (phrases) => {
      await phraseRepo.upsert(
        phraseFromKept({
          text: offer.sentence,
          categoryId: offer.categoryId,
          phrases,
          nowMs: Date.now(),
        }),
      );
      // Kept, so the offer is spent. The tile existing is the confirmation; a message would need room
      // this band does not have, and the band's height is the one thing it may never spend.
      useOfferState.setState({ offer: null });
    });
  };

  return (
    <ActivateButton
      id="action:keepSentence"
      onActivate={keep}
      label={de.keepSentenceName}
      style={{
        flex: '1 1 0',
        minWidth: 0,
        height: '100%',
        fontSize: 'var(--font-body)',
        fontWeight: 700,
        background: 'var(--key-bg)',
        color: 'var(--key-fg)',
        borderWidth: 3,
        borderColor: 'var(--key-border)',
        borderStyle: 'dashed',
        // A pill, like the layer switch: shape says *this one does not speak*, which is worth more than
        // any label to someone who is tired and works under any colour vision (§5.2, `GEO-15`). Dashed
        // rather than solid because, unlike the switch, it is here for a moment and then gone.
        borderRadius: 999,
      }}
    >
      {de.keepSentence}
    </ActivateButton>
  );
}
